package top.wcpe.allincore.dsl.ir.canvas

import kotlin.jvm.JvmStatic

/**
 * draw 指令的工厂方法集合。
 *
 * 从原 [DrawBlob] 的工厂方法（valAssign / forBegin / arc / ring / ...）
 * 拆出（M11.x 重构）；[DrawBlob] 仍以静态方法转发，保持调用方零改动。
 */
object InstructionFactory {

    // ===== M11.3 控制流 Instruction 工厂 =====

    /** 构造 VAL_ASSIGN 指令；value 可为 LITERAL 或 EXPR。 */
    @JvmStatic
    fun valAssign(slotIndex: Int, value: Operand): Instruction {
        checkSlot(slotIndex)
        return Instruction(
            DrawOpcodes.OP_VAL_ASSIGN,
            arrayOf(Operand.literal(slotIndex.toDouble()), value),
        )
    }

    /** 构造 FOR_BEGIN 指令；bodyByteLength 由编码阶段回填，可先填 0。 */
    @JvmStatic
    fun forBegin(slotIndex: Int, iterCount: Int, bodyByteLength: Int): Instruction {
        checkSlot(slotIndex)
        if (iterCount < 0 || iterCount > 0xFFFF) {
            throw IllegalArgumentException("iterCount 越界: $iterCount")
        }
        if (bodyByteLength < 0 || bodyByteLength > 0xFFFF) {
            throw IllegalArgumentException("bodyByteLength 越界: $bodyByteLength")
        }
        return Instruction(
            DrawOpcodes.OP_FOR_BEGIN,
            arrayOf(
                Operand.literal(slotIndex.toDouble()),
                Operand.literal(iterCount.toDouble()),
                Operand.literal(bodyByteLength.toDouble()),
            ),
        )
    }

    /** 构造 FOR_END 标记指令。 */
    @JvmStatic
    fun forEnd(): Instruction = Instruction(DrawOpcodes.OP_FOR_END, emptyArray<Operand>())

    /** 构造 IF_BEGIN 指令；then/elseByteLength 由编码阶段回填，可先填 0。 */
    @JvmStatic
    fun ifBegin(condition: Operand, thenByteLength: Int, elseByteLength: Int): Instruction {
        if (thenByteLength < 0 || thenByteLength > 0xFFFF) {
            throw IllegalArgumentException("thenByteLength 越界: $thenByteLength")
        }
        if (elseByteLength < 0 || elseByteLength > 0xFFFF) {
            throw IllegalArgumentException("elseByteLength 越界: $elseByteLength")
        }
        return Instruction(
            DrawOpcodes.OP_IF_BEGIN,
            arrayOf(
                condition,
                Operand.literal(thenByteLength.toDouble()),
                Operand.literal(elseByteLength.toDouble()),
            ),
        )
    }

    /** 构造 IF_END 标记指令。 */
    @JvmStatic
    fun ifEnd(): Instruction = Instruction(DrawOpcodes.OP_IF_END, emptyArray<Operand>())

    // ===== M11.4 高级图元指令工厂 =====

    /** 构造 ARC 指令：cx, cy, radius, startAngle, endAngle, color。 */
    @JvmStatic
    fun arc(cx: Double, cy: Double, radius: Double, startAngle: Double, endAngle: Double, color: Int): Instruction =
        Instruction(
            DrawOpcodes.OP_ARC,
            arrayOf(
                Operand.literal(cx),
                Operand.literal(cy),
                Operand.literal(radius),
                Operand.literal(startAngle),
                Operand.literal(endAngle),
                Operand.literal(color.toDouble()),
            ),
        )

    /** 构造 RING 指令：cx, cy, radius, width, color。 */
    @JvmStatic
    fun ring(cx: Double, cy: Double, radius: Double, width: Double, color: Int): Instruction =
        Instruction(
            DrawOpcodes.OP_RING,
            arrayOf(
                Operand.literal(cx),
                Operand.literal(cy),
                Operand.literal(radius),
                Operand.literal(width),
                Operand.literal(color.toDouble()),
            ),
        )

    /** 构造 ROUND_RECT 指令：x, y, w, h, radius, color。 */
    @JvmStatic
    fun roundRect(x: Double, y: Double, w: Double, h: Double, radius: Double, color: Int): Instruction =
        Instruction(
            DrawOpcodes.OP_ROUND_RECT,
            arrayOf(
                Operand.literal(x),
                Operand.literal(y),
                Operand.literal(w),
                Operand.literal(h),
                Operand.literal(radius),
                Operand.literal(color.toDouble()),
            ),
        )

    /** 构造 TEXT 指令：x, y, size, color + content 字符串。 */
    @JvmStatic
    fun text(x: Double, y: Double, size: Double, color: Int, content: String): Instruction =
        Instruction(
            DrawOpcodes.OP_TEXT,
            arrayOf(
                Operand.literal(x),
                Operand.literal(y),
                Operand.literal(size),
                Operand.literal(color.toDouble()),
                Operand.str(content),
            ),
        )

    /**
     * 构造 POLYGON 指令：vertices = [x0, y0, x1, y1, ..., xN-1, yN-1]，N >= 3。
     * 内部 operands 共 (2*N + 1) 项：前 2N 为顶点对，末位为 color。
     */
    @JvmStatic
    fun polygon(vertices: DoubleArray, color: Int): Instruction {
        val vertexCount = vertices.size / 2
        if (vertexCount < 3) {
            throw IllegalArgumentException("POLYGON 至少需要 3 个顶点，实际: $vertexCount")
        }
        val ops = Array<Operand>(vertices.size + 1) { i ->
            if (i < vertices.size) Operand.literal(vertices[i]) else Operand.literal(color.toDouble())
        }
        return Instruction(DrawOpcodes.OP_POLYGON, ops)
    }

    /**
     * 构造 PATH 指令：vertices = [x0, y0, x1, y1, ..., xN-1, yN-1]，N >= 2。
     * 内部 operands 共 (2*N + 2) 项：前 2N 为顶点对，末两位依次为 width、color；
     * closed 标志通过 [Instruction.isPathClosed] 暴露，不进 operands 数组。
     */
    @JvmStatic
    fun path(vertices: DoubleArray, width: Double, color: Int, closed: Boolean): Instruction {
        val vertexCount = vertices.size / 2
        if (vertexCount < 2) {
            throw IllegalArgumentException("PATH 至少需要 2 个顶点，实际: $vertexCount")
        }
        val ops = Array<Operand>(vertices.size + 2) { i ->
            when (i) {
                vertices.size -> Operand.literal(width)
                vertices.size + 1 -> Operand.literal(color.toDouble())
                else -> Operand.literal(vertices[i])
            }
        }
        return Instruction(DrawOpcodes.OP_PATH, ops, closed)
    }

    /** 构造 DRAW_IMAGE 指令：x, y, w, h + resourceId 字符串。 */
    @JvmStatic
    fun drawImage(x: Double, y: Double, w: Double, h: Double, resourceId: String): Instruction =
        Instruction(
            DrawOpcodes.OP_DRAW_IMAGE,
            arrayOf(
                Operand.literal(x),
                Operand.literal(y),
                Operand.literal(w),
                Operand.literal(h),
                Operand.str(resourceId),
            ),
        )

    /** v4 B.3：构造 BEZIER_QUAD 指令：p0x, p0y, c1x, c1y, p1x, p1y, width, color。 */
    @JvmStatic
    fun bezierQuad(
        p0x: Double,
        p0y: Double,
        c1x: Double,
        c1y: Double,
        p1x: Double,
        p1y: Double,
        width: Double,
        color: Int,
    ): Instruction =
        Instruction(
            DrawOpcodes.OP_BEZIER_QUAD,
            arrayOf(
                Operand.literal(p0x), Operand.literal(p0y),
                Operand.literal(c1x), Operand.literal(c1y),
                Operand.literal(p1x), Operand.literal(p1y),
                Operand.literal(width), Operand.literal(color.toDouble()),
            ),
        )

    /** v4 B.3：构造 BEZIER_CUBIC 指令：p0x, p0y, c1x, c1y, c2x, c2y, p1x, p1y, width, color。 */
    @JvmStatic
    fun bezierCubic(
        p0x: Double,
        p0y: Double,
        c1x: Double,
        c1y: Double,
        c2x: Double,
        c2y: Double,
        p1x: Double,
        p1y: Double,
        width: Double,
        color: Int,
    ): Instruction =
        Instruction(
            DrawOpcodes.OP_BEZIER_CUBIC,
            arrayOf(
                Operand.literal(p0x), Operand.literal(p0y),
                Operand.literal(c1x), Operand.literal(c1y),
                Operand.literal(c2x), Operand.literal(c2y),
                Operand.literal(p1x), Operand.literal(p1y),
                Operand.literal(width), Operand.literal(color.toDouble()),
            ),
        )

    /**
     * v4 B.4：构造 PATH_BUILD 指令。
     *
     * @param kinds 节点种类数组（PATH_NODE_*），首项必须是 MoveTo
     * @param coordOperands 节点坐标 operands（按 kinds 顺序展开；MoveTo/LineTo 各 2 个、QuadTo 4 个、
     *                      CubicTo 6 个、Close 0 个）
     * @param mode  0=stroke, 1=fill_nonzero, 2=fill_evenodd
     * @param width 描边宽度（fill 模式下仍占位但被忽略）
     * @param color ARGB int
     */
    @JvmStatic
    fun pathBuild(kinds: ByteArray, coordOperands: Array<Operand>, mode: Int, width: Double, color: Int): Instruction {
        val operands = Array<Operand>(coordOperands.size + 2) { i ->
            when (i) {
                coordOperands.size -> Operand.literal(width)
                coordOperands.size + 1 -> Operand.literal(color.toDouble())
                else -> coordOperands[i]
            }
        }
        return Instruction(DrawOpcodes.OP_PATH_BUILD, operands, kinds, mode.toByte())
    }

    /**
     * v4 B.5：构造 OP_BITMAP 指令（像素位图，小地图前置）。
     *
     * operands 数组按 spec §4.2 顺序：data(OPERAND_BYTES)、srcW、srcH、paletteId、
     * dstX、dstY、dstW、dstH，共 8 个。
     *
     * @param data      像素数据 byte[]，长度不超过 65535（u16 LP 限制）
     * @param srcW      源像素宽
     * @param srcH      源像素高
     * @param paletteId 调色板 ID（0 为灰度近似 fallback）
     * @param dstX      目标矩形左上角 X
     * @param dstY      目标矩形左上角 Y
     * @param dstW      目标矩形宽
     * @param dstH      目标矩形高
     */
    @JvmStatic
    fun bitmap(
        data: ByteArray,
        srcW: Int,
        srcH: Int,
        paletteId: Int,
        dstX: Double,
        dstY: Double,
        dstW: Double,
        dstH: Double,
    ): Instruction =
        Instruction(
            DrawOpcodes.OP_BITMAP,
            arrayOf(
                Operand.bytes(data),
                Operand.literal(srcW.toDouble()),
                Operand.literal(srcH.toDouble()),
                Operand.literal(paletteId.toDouble()),
                Operand.literal(dstX),
                Operand.literal(dstY),
                Operand.literal(dstW),
                Operand.literal(dstH),
            ),
        )

    private fun checkSlot(slotIndex: Int) {
        if (slotIndex < 0 || slotIndex >= DrawOpcodes.LOCAL_SLOT_COUNT) {
            throw IllegalArgumentException(
                "slotIndex 必须在 [0,${DrawOpcodes.LOCAL_SLOT_COUNT}) 内: $slotIndex",
            )
        }
    }
}
